import { currentSeason } from './rotationScore.js'
import { isGymWearLog } from './gymShelf.js'
import { complimentCountForLog } from './complimentEvents.js'
import { VIBE_PRESETS, performanceSignal, vibePreset } from './vibeShelves.js'
import { leaningDistance } from './fragranceLeaning.js'

// Situation kinds are the original nine. Accord and Vibe are parameterised
// kinds: `param` names the accord or the vibe preset.
export const SITUATION_KINDS = Object.freeze([
  ['gym', 'Gym'], ['day', 'Day'], ['evening', 'Evening'],
  ['most_worn', 'Most Worn'], ['season', 'Season'], ['date', 'Date Night'],
  ['clubbing', 'Clubbing'], ['school', 'School'], ['on_the_job', 'On the Job'],
  ['vacation', 'Vacation'], ['custom', 'Custom'],
])
export const GATEWAY_KINDS = Object.freeze([...SITUATION_KINDS, ['accord', 'Accord'], ['vibe', 'Vibe']])
export const GATEWAY_FAMILIES = Object.freeze([
  Object.freeze({ id: 'situation', label: 'Situation', kinds: SITUATION_KINDS }),
  Object.freeze({ id: 'accord', label: 'Accord', kinds: Object.freeze([['accord', 'Accord']]) }),
  Object.freeze({ id: 'vibe', label: 'Vibe', kinds: Object.freeze([['vibe', 'Vibe']]) }),
])
export const MAX_GATEWAYS = 16
export const MIN_GATEWAYS = 1
export const ABOVE_FOLD_GATEWAYS = 6
const PARAM_KINDS = new Set(['accord', 'vibe'])

const normalize = value => String(value || '').trim().toLocaleLowerCase()
const words = value => normalize(value).split(/[^\p{L}\p{N}]+/u).filter(word => word.length > 2)
const asArray = value => Array.isArray(value) ? value : typeof value === 'string' ? value.split(',').map(item => item.trim()) : []
const active = fragrance => !fragrance?.finished && !fragrance?.finished_at && !fragrance?.given_away && !fragrance?.deleted_at && !fragrance?.archived_at
const wearDay = log => log?.worn_on || log?.wear_date || ''
const wearTime = log => Date.parse(log?.applications?.[0]?.applied_at || log?.created_at || `${wearDay(log)}T12:00:00`) || 0
const kindLabel = kind => GATEWAY_KINDS.find(([key]) => key === kind)?.[1] || ''
const titleCase = value => String(value || '').replace(/\b\p{L}/gu, letter => letter.toLocaleUpperCase())

// Default tile kinds each defaults version introduced. A saved row records the
// version it has been merged up to; only kinds introduced in a newer version
// are offered to it, so a classic default the user removed (Date Night, say)
// is never re-added. Version 1 is the collection-based Accord shelves, which
// until then only reached accounts that had never saved tiles. To introduce a
// new default: add it to defaultGateways() and list its kind under the next
// version here.
export const DEFAULTS_INTRODUCED = Object.freeze({ 1: Object.freeze(['accord']) })
export const HOME_DEFAULTS_VERSION = Math.max(...Object.keys(DEFAULTS_INTRODUCED).map(Number))
const DEFAULTS_VERSION_KEY = '_defaults_version'

export const appliedDefaultsVersion = shelves => Number.parseInt(shelves?.[DEFAULTS_VERSION_KEY]?.[0], 10) || 0

// The first six situation kinds are the long-standing defaults.
export const DEFAULT_GATEWAYS = Object.freeze(
  SITUATION_KINDS.slice(0, 6).map(([kind, label], slot) => Object.freeze({ id: kind, slot, kind, label, purpose: '', param: '' })),
)

export function gatewayTitle(tile, season = currentSeason()) {
  return tile.kind === 'season' && (!tile.label || tile.label === 'Season') ? season : tile.label
}

export function standardGatewayLabel(kind, param = '') {
  if (kind === 'accord') return titleCase(param) || 'Accord'
  if (kind === 'vibe') return vibePreset(param)?.label || 'Vibe'
  return kindLabel(kind)
}

function normalizeParam(kind, value) {
  if (kind === 'accord') return normalize(value).slice(0, 40)
  if (kind === 'vibe') return vibePreset(normalize(value)) ? normalize(value) : ''
  return ''
}

// Stable per-tile id: kind for singleton situation kinds, kind:param for
// accord and vibe shelves, and a numbered suffix when a kind repeats.
export function gatewayTileId(kind, param = '', taken = new Set()) {
  const base = PARAM_KINDS.has(kind) && param ? `${kind}:${param}` : kind
  let id = base
  for (let n = 2; taken.has(id); n += 1) id = `${base}#${n}`
  return id
}

function normalizeTile(item, index, taken) {
  const kind = GATEWAY_KINDS.some(([key]) => key === item?.kind) ? item.kind : null
  if (!kind) return null
  const param = normalizeParam(kind, item.param)
  if (PARAM_KINDS.has(kind) && !param) return null
  const standard = standardGatewayLabel(kind, param)
  const requested = typeof item.id === 'string' && /^[a-z_]+(:[^#]{1,40})?(#\d+)?$/u.test(item.id) ? item.id : ''
  const id = requested && !taken.has(requested) ? requested : gatewayTileId(kind, param, taken)
  taken.add(id)
  const tile = {
    id, slot: index, kind,
    label: String(item.label || standard).trim().slice(0, 28) || standard,
    purpose: String(item.purpose || '').trim().slice(0, 120),
    param,
  }
  // A saved row from the six-slot era carries its slot and no id at all;
  // remember it so its shelf key can migrate once.
  if (!('id' in item) && Number.isInteger(item.slot) && item.slot >= 0 && item.slot <= 5) tile.legacy_key = `${item.slot}:${kind}`
  return tile
}

// Tiles are an ordered list of 1..16. Saved six-slot rows migrate by their
// slot order; anything past the cap is dropped, never reordered.
export function normalizeGateways(value) {
  const saved = Array.isArray(value) ? value.filter(row => row && typeof row === 'object') : []
  // A six-slot era payload (rows with a slot and no id) always meant six
  // tiles, missing slots falling back to the defaults of that era.
  const legacyPayload = saved.every(row => !('id' in row) && Number.isInteger(row.slot))
  const ordered = legacyPayload
    ? DEFAULT_GATEWAYS.map((fallback, slot) => saved.find(row => row.slot === slot) || { slot, kind: fallback.kind, label: fallback.label, purpose: '' })
    : saved
  // Migrate 'compliments' (Most Complimented) to 'vibe:compliment_getters'
  const migrated = ordered.map(row => {
    if (row?.kind === 'compliments') {
      return { ...row, kind: 'vibe', param: 'compliment_getters', label: row.label === 'Most Complimented' ? 'Compliment Getters' : row.label }
    }
    return row
  })
  const taken = new Set()
  const tiles = migrated.map(row => normalizeTile(row, 0, taken)).filter(Boolean).slice(0, MAX_GATEWAYS).map((tile, index) => ({ ...tile, slot: index }))
  return tiles.length >= MIN_GATEWAYS ? tiles : DEFAULT_GATEWAYS.map(tile => ({ ...tile, legacy_key: `${tile.slot}:${tile.kind}` }))
}

export const gatewayShelfKey = tile => tile.id || gatewayTileId(tile.kind, tile.param)

const LEGACY_KEY = /^[0-5]:(compliments|gym|day|evening|most_worn|season|date|clubbing|custom)$/
const TILE_KEY = /^[a-z_]+(:[^#]{1,40})?(#\d+)?$/u

// Shelves are keyed by tile id. Legacy `slot:kind` keys migrate to the id of
// the tile that occupied that slot so no saved shelf is lost. The reserved key
// _defaults_version (the defaults version this row has merged) is preserved.
export function normalizeShelves(value, tiles = []) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const legacy = new Map(tiles.filter(tile => tile.legacy_key).map(tile => [tile.legacy_key, tile.id]))
  const shelves = {}
  for (const [key, ids] of Object.entries(value)) {
    // Preserve the reserved defaults version key
    if (key === DEFAULTS_VERSION_KEY) {
      if (Array.isArray(ids) && ids.length) shelves[key] = [String(ids[0])]
      continue
    }
    if (!Array.isArray(ids)) continue
    // Migrate 'compliments' shelf key to 'vibe:compliment_getters'
    const migratedKey = key === 'compliments' ? 'vibe:compliment_getters' : key
    const target = LEGACY_KEY.test(migratedKey) ? legacy.get(migratedKey) : TILE_KEY.test(migratedKey) ? migratedKey : null
    if (!target) continue
    const clean = [...new Set(ids.filter(id => typeof id === 'string'))]
    shelves[target] = shelves[target] ? [...new Set([...shelves[target], ...clean])] : clean
  }
  return shelves
}

// One normalization for the persisted payload: tiles first, then shelves keyed
// by those tiles. Persisted tiles never carry the transient legacy_key.
export function normalizeGatewayPreferences(value) {
  const tiles = normalizeGateways(value?.tiles)
  const shelves = normalizeShelves(value?.shelves, tiles)
  return { tiles: tiles.map(({ legacy_key, ...tile }) => tile), shelves }
}

export const hasSavedGateways = value => Array.isArray(value?.tiles) && value.tiles.length > 0

// Merge defaults introduced after the saved row's version into saved tiles.
// Appends, after the user's own tiles and capped at MAX_GATEWAYS, each default
// tile whose kind was introduced in a version newer than `appliedVersion` and
// which the user does not already have any tile of that kind for. A user who
// already has an Accord shelf chose their accords themselves. Never removes or
// reorders user tiles. Returns {tiles, shelves, changed}; `changed` means the
// row (tiles and/or the version marker) needs persisting.
export function mergeDefaultGateways({ tiles, shelves, fragrances, appliedVersion }) {
  const current = Number.isInteger(appliedVersion) ? appliedVersion : 0
  if (current >= HOME_DEFAULTS_VERSION) return { tiles, shelves, changed: false }

  const introduced = new Set(Object.entries(DEFAULTS_INTRODUCED)
    .filter(([version]) => Number(version) > current)
    .flatMap(([, kinds]) => kinds))
  const savedKinds = new Set(tiles.map(tile => tile.kind))
  const missing = defaultGateways(fragrances)
    .filter(tile => introduced.has(tile.kind) && !savedKinds.has(tile.kind))
    .map(({ kind, param }) => ({ kind, param }))

  const nextShelves = { ...shelves, [DEFAULTS_VERSION_KEY]: [String(HOME_DEFAULTS_VERSION)] }
  const nextTiles = missing.length ? normalizeGateways([...tiles, ...missing].slice(0, MAX_GATEWAYS)) : tiles
  return { tiles: nextTiles, shelves: nextShelves, changed: true }
}

// Accords present on 2+ active bottles, most frequent first.
export function collectionAccords(fragrances = [], minimum = 2) {
  const counts = new Map()
  for (const fragrance of fragrances.filter(active)) {
    for (const accord of new Set(asArray(fragrance.accord_tags).map(normalize).filter(Boolean))) counts.set(accord, (counts.get(accord) || 0) + 1)
  }
  return [...counts.entries()].filter(([, count]) => count >= minimum)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([accord, count]) => ({ accord, count }))
}

// Active bottles with no scent tags at all: neither accords nor vibe tags.
export function untaggedCount(fragrances = [], kind = 'accord') {
  return fragrances.filter(active).filter(fragrance => !asArray(fragrance.accord_tags).some(Boolean)
    && (kind !== 'vibe' || !effectiveVibeTags(fragrance).length)).length
}

// New-user defaults: Most Worn, Gym, Day, Evening, plus the two
// most frequent accords when the collection has 3+ tagged bottles.
export function defaultGateways(fragrances = []) {
  const tagged = fragrances.filter(active).filter(fragrance => asArray(fragrance.accord_tags).some(Boolean)).length
  const accords = tagged >= 3 ? collectionAccords(fragrances).slice(0, 2) : []
  if (!accords.length) return DEFAULT_GATEWAYS.map(tile => ({ ...tile }))
  return normalizeGateways([
    { kind: 'most_worn' }, { kind: 'gym' }, { kind: 'day' }, { kind: 'evening' },
    ...accords.map(({ accord }) => ({ kind: 'accord', param: accord })),
  ])
}

export function addGatewayTile(tiles, kind, param = '') {
  if (tiles.length >= MAX_GATEWAYS) return tiles
  return normalizeGateways([...tiles, { kind, param }])
}

export function removeGatewayTile(tiles, id) {
  const remaining = tiles.filter(tile => tile.id !== id)
  return remaining.length >= MIN_GATEWAYS ? remaining.map((tile, slot) => ({ ...tile, slot })) : tiles
}

function matchingLogs(fragrance, logs) {
  const ids = new Set([fragrance.id, ...(fragrance.sizes || []).map(size => size.id)])
  return [...ids].flatMap(id => logs.get(id) || [])
}

function contextFor(tile, selectedSeason) {
  switch (tile.kind) {
    case 'gym': return ['gym', 'sport', 'workout', 'fitness']
    case 'day': return ['day', 'daytime', 'daily', 'casual', 'errands', 'office', 'school', 'work']
    case 'evening': return ['evening', 'night', 'dinner']
    case 'date': return ['date', 'romantic', 'evening']
    case 'clubbing': return ['club', 'clubbing', 'party', 'night out']
    case 'school': return ['school', 'class', 'campus', 'university', 'college']
    case 'on_the_job': return ['work', 'job', 'blue collar', 'work site', 'construction', 'manual labor', 'field work']
    case 'vacation': return ['vacation', 'holiday', 'travel', 'trip', 'getaway']
    case 'season': return [normalize(selectedSeason)]
    case 'custom': return words(`${tile.purpose} ${tile.label}`)
    case 'vibe': return [...(vibePreset(tile.param)?.contexts || [])]
    case 'accord': return []
    default: return words(tile.purpose)
  }
}

// Match complete words/phrases in structured context, never substrings such as
// "work" in "workout", or arbitrary prose in a memory/note.
const matches = (text, term) => ` ${words(text).join(' ')} `.includes(` ${words(term).join(' ')} `)
const seasonName = value => normalize(value).replace('autumn', 'fall')
const contextText = log => [log.occasion, log.wear_label, log.evening_context,
  ...asArray(log.context_tags).map(tag => tag?.context_tag || tag)].filter(Boolean).join(' ')

function logFits(tile, log, terms, region) {
  const text = contextText(log)
  const gym = isGymWearLog(log) || ['gym', 'workout', 'fitness'].some(term => matches(text, term))
  if (tile.kind === 'gym') return gym
  if (tile.kind === 'season') {
    const day = wearDay(log)
    const date = /^\d{4}-\d{2}-\d{2}$/.test(day) ? new Date(`${day}T12:00:00`) : null
    const recorded = log.season || (date && Number.isFinite(date.getTime()) ? currentSeason(date, region) : '')
    return seasonName(recorded) === seasonName(terms[0])
  }
  if (tile.kind === 'day' || tile.kind === 'evening') {
    if (gym) return false
    const explicitEvening = ['evening', 'night', 'dinner', 'date', 'clubbing', 'party'].some(term => matches(text, term))
    const explicitDay = ['day', 'daytime', 'daily', 'office', 'school', 'work'].some(term => matches(text, term))
    if (explicitEvening) return tile.kind === 'evening'
    if (explicitDay) return tile.kind === 'day'
    // The recorded wear context wins; created_at is an entry time, not wear time.
    const bucket = normalize(log.time_bucket)
    if (['morning', 'afternoon', 'day'].includes(bucket)) return tile.kind === 'day'
    if (['evening', 'night'].includes(bucket)) return tile.kind === 'evening'
    return tile.kind === 'day' && ['casual', 'errands'].some(term => matches(text, term))
  }
  return terms.some(term => matches(text, term))
}

// Effective vibe tags as exposed by collection_display.vibe_tags
// (FOS-CATALOG-VIBE-TAGS-001): [{tag, confidence, source, creators}].
export function effectiveVibeTags(fragrance) {
  const raw = fragrance?.vibe_tags
  const list = Array.isArray(raw) ? raw : typeof raw === 'string' ? (() => { try { return JSON.parse(raw) } catch { return [] } })() : []
  return (Array.isArray(list) ? list : []).map(item => typeof item === 'string' ? { tag: item, confidence: 1 } : item)
    .filter(item => item && typeof item.tag === 'string')
    .map(item => ({ tag: normalize(item.tag), confidence: Math.max(0, Math.min(1, Number(item.confidence ?? 1) || 0)), source: item.source || '', creators: Number(item.creators || 0) }))
}

const noteWords = fragrance => [fragrance.all_notes, ...asArray(fragrance.top_notes), ...asArray(fragrance.mid_notes),
  ...asArray(fragrance.base_notes), ...asArray(fragrance.notes)].filter(Boolean).join(', ')

function scentMatches(fragrance, accords, notes) {
  const bottleAccords = new Set(asArray(fragrance.accord_tags).map(normalize).filter(Boolean))
  const matchedAccords = accords.filter(accord => bottleAccords.has(normalize(accord)))
  const notesText = noteWords(fragrance)
  const matchedNotes = notes.filter(note => matches(notesText, note))
  return { matchedAccords, matchedNotes }
}

const plural = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`
const joinEvidence = list => list.slice(0, 3).map(titleCase).join(', ')

export function rankGatewayFragrances({ tile, fragrances = [], logs = [], season = currentSeason(), region = '', temperatureC = null, excludedIds = [], leaningPreference = null }) {
  const terms = contextFor(tile, season)
  const preset = tile.kind === 'vibe' ? vibePreset(tile.param) : null
  const historyByFragrance = new Map()
  for (const log of logs) {
    const history = historyByFragrance.get(log.fragrance_id) || []
    history.push(log)
    historyByFragrance.set(log.fragrance_id, history)
  }
  const excluded = new Set(excludedIds)
  const scentKind = tile.kind === 'accord' || (tile.kind === 'vibe' && preset && !preset.complimentsOnly)
  return fragrances.filter(fragrance => active(fragrance) && !excluded.has(fragrance.id))
    .map(fragrance => {
      const history = matchingLogs(fragrance, historyByFragrance)
      const fitted = history.filter(log => logFits(tile, log, terms, region))
      const compliments = history.reduce((count, log) => count + complimentCountForLog(log), 0)
      const tags = [...asArray(fragrance.occasions), fragrance.occasion,
        ...asArray(fragrance.seasons), fragrance.peak_season,
        ...['Spring', 'Summer', 'Fall', 'Winter'].filter(key => fragrance[key.toLowerCase()] === true),
        ...history.flatMap(log => asArray(log.context_tags).map(tag => tag?.context_tag || tag)),
      ].filter(Boolean).join(' ')
      const matchedTags = scentKind ? [] : terms.filter(term => matches(seasonName(tags), seasonName(term)))
      const fragranceSeasons = [...asArray(fragrance.seasons), fragrance.peak_season,
        ...['Spring', 'Summer', 'Fall', 'Winter'].filter(key => fragrance[key.toLowerCase()] === true)].filter(Boolean).join(' ')
      const seasonFit = matches(seasonName(fragranceSeasons), seasonName(season)) ? 1 : 0
      const climate = Number.isFinite(temperatureC) ? temperatureC >= 24 ? 'Summer' : temperatureC <= 10 ? 'Winter' : null : null
      const weatherFit = climate && matches(fragranceSeasons, climate) ? 1 : 0

      let primaryCount = 0
      let score = matchedTags.length * 4 + seasonFit * 2 + weatherFit
      let reason = 'No recorded match for this purpose'
      let tagConfidence = -1
      let tagged = 0
      const metricOnly = ['compliments', 'most_worn'].includes(tile.kind) || Boolean(preset?.complimentsOnly)
      const label = tile.kind === 'season' ? season : tile.label || standardGatewayLabel(tile.kind, tile.param) || 'this purpose'
      const evidence = []

      if (tile.kind === 'compliments' || preset?.complimentsOnly) {
        primaryCount = compliments
        if (compliments) reason = `${plural(compliments, 'recorded compliment')}`
      } else if (tile.kind === 'most_worn') {
        primaryCount = history.length
        if (history.length) reason = `${plural(history.length, 'recorded wear')}`
      } else if (tile.kind === 'accord') {
        const { matchedAccords, matchedNotes } = scentMatches(fragrance, [tile.param], [tile.param])
        primaryCount = matchedAccords.length + matchedNotes.length
        score = matchedAccords.length * 4 + matchedNotes.length * 2 + fitted.length * 2 + seasonFit * 2 + weatherFit
        if (matchedAccords.length) evidence.push(`${titleCase(tile.param)} accord`)
        else if (matchedNotes.length) evidence.push(`${titleCase(tile.param)} notes`)
        if (fitted.length) evidence.push(plural(fitted.length, `${label} wear`))
        if (evidence.length) reason = evidence.join(' · ')
      } else if (tile.kind === 'vibe' && preset) {
        const effective = effectiveVibeTags(fragrance)
        const hit = effective.filter(item => preset.tags.includes(item.tag)).sort((a, b) => b.confidence - a.confidence)[0]
        const weights = preset.weights
        const performance = preset.performance ? performanceSignal(history) : null
        tagged = effective.length ? 1 : 0
        if (hit) {
          // Tags first: a bottle with an effective tag outranks any fallback match.
          tagConfidence = hit.confidence
          primaryCount = 1
          score = Math.round(hit.confidence * 100) + fitted.length * weights.wear + compliments * weights.compliment + seasonFit * 2 + weatherFit
          evidence.push(hit.creators >= 2 ? `Named ${preset.label.toLocaleLowerCase()} by ${hit.creators} creators` : `Tagged ${preset.label.toLocaleLowerCase()}`)
        } else if (!effective.length) {
          // Fallback rule table only while the bottle has no effective tags.
          const { matchedAccords, matchedNotes } = scentMatches(fragrance, preset.accords, preset.notes)
          const beast = performance?.beast ? 1 : 0
          primaryCount = matchedAccords.length + matchedNotes.length + beast
          score = matchedAccords.length * weights.accord + matchedNotes.length * weights.note + fitted.length * weights.wear
            + compliments * weights.compliment + beast * 4 + seasonFit * 2 + weatherFit
          if (preset.lowProjection && Number(fragrance.projection_score) > 0) {
            if (Number(fragrance.projection_score) <= 2) score += 2
            else if (Number(fragrance.projection_score) >= 4) score -= 4
          }
          if (beast) evidence.push(performance.strong ? plural(performance.strong, 'strong check-in') : `${Math.round(performance.averageHours)}h logged`)
          if (matchedAccords.length) evidence.push(joinEvidence(matchedAccords))
          else if (matchedNotes.length) evidence.push(joinEvidence(matchedNotes))
        }
        if (fitted.length) evidence.push(plural(fitted.length, `${label} wear`))
        if (compliments && primaryCount) evidence.push(plural(compliments, 'compliment'))
        if (evidence.length) reason = evidence.join(' · ')
      } else {
        primaryCount = fitted.length
        if (fitted.length) reason = plural(fitted.length, `${label} wear`)
        else if (matchedTags.length) reason = `Tagged for ${matchedTags.join(', ')}`
      }

      const supported = primaryCount > 0 || (!metricOnly && !scentKind && matchedTags.length > 0)
      return { fragrance, primaryCount, supported, score, reason, wearCount: history.length, fitCount: fitted.length, compliments,
        tagConfidence, tagged, leaningDistance: leaningDistance(fragrance, leaningPreference), lastWorn: Math.max(0, ...history.map(wearTime)) }
    })
    // Exact recorded count is the primary criterion. Secondary fit cannot
    // overtake it. No random shuffle, neglect bonus, or reapplication bonus.
    // Vibe shelves rank effective tags (by confidence) ahead of fallback rules.
    .sort((a, b) => (tile.kind === 'vibe' ? b.tagConfidence - a.tagConfidence : 0)
      || b.primaryCount - a.primaryCount || Number(b.supported) - Number(a.supported)
      || b.score - a.score || b.wearCount - a.wearCount || b.lastWorn - a.lastWorn
      || a.leaningDistance - b.leaningDistance
      || a.fragrance.name.localeCompare(b.fragrance.name) || a.fragrance.id.localeCompare(b.fragrance.id))
}

export function gatewayLeader(ranked) {
  return ranked.find(item => item.primaryCount > 0) || null
}

// A curated shelf owns its preview, including an intentionally empty shelf.
export function gatewayPreview({ tile, savedIds, ranked, gymPinnedIds = [] }) {
  if (Array.isArray(savedIds) || (tile.kind === 'gym' && gymPinnedIds.length)) {
    const first = gatewayShelf({ tile, savedIds, ranked, gymPinnedIds })[0]
    return first ? { ...first, reason: 'First on your shelf' } : null
  }
  return gatewayLeader(ranked)
}

export function gatewayShelf({ tile, savedIds, ranked, gymPinnedIds = [], limit = 4 }) {
  const ids = Array.isArray(savedIds) ? savedIds : tile.kind === 'gym' && gymPinnedIds.length ? gymPinnedIds : null
  if (ids) {
    const byId = new Map(ranked.map(item => [item.fragrance.id, item]))
    return ids.map(id => byId.get(id)).filter(Boolean).map(item => item.supported ? item : { ...item, reason: 'Saved by you' })
  }
  const complimentsOnly = tile.kind === 'compliments' || Boolean(vibePreset(tile.param)?.complimentsOnly && tile.kind === 'vibe')
  return ranked.filter(item => complimentsOnly
    ? item.compliments > 0
    : tile.kind === 'most_worn'
      ? item.wearCount > 0
      : tile.kind === 'accord' || tile.kind === 'vibe'
        ? item.primaryCount > 0
        : item.fitCount > 0).slice(0, limit)
}

// Pre-fill for a new accord or vibe shelf: the top supported matches.
export function gatewaySuggestions(ranked, limit = 6) {
  return ranked.filter(item => item.supported && item.primaryCount > 0).slice(0, limit)
}

export function gatewayIdeas(ranked, shelf, page = 0, pageSize = 6) {
  const shelfIds = new Set(shelf.map(item => item.fragrance.id))
  const candidates = ranked.filter(item => item.supported && !shelfIds.has(item.fragrance.id))
  const pages = Math.max(1, Math.ceil(candidates.length / pageSize))
  return { items: candidates.slice((page % pages) * pageSize, ((page % pages) + 1) * pageSize), pages }
}

export { VIBE_PRESETS }
